import { richNodes, setRich } from '../icons/dom';

type Child = Node | string | number | null | undefined | false;

export interface ElProps {
  class?: string;
  text?: string;
  title?: string;
  attrs?: Record<string, string>;
  style?: Partial<CSSStyleDeclaration>;
  on?: { [K in keyof HTMLElementEventMap]?: (e: HTMLElementEventMap[K]) => void };
  disabled?: boolean;
  hidden?: boolean;
}

/**
 * Tiny hyperscript helper: h('div', { class: 'x' }, child, child). Text (the `text` prop and
 * string children) draws its `:icon:` tokens as icons.
 */
export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: ElProps = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (props.class) el.className = props.class;
  if (props.text !== undefined) setRich(el, props.text);
  if (props.title) el.title = props.title;
  if (props.hidden) el.hidden = true;
  if (props.disabled !== undefined && 'disabled' in el) (el as HTMLButtonElement).disabled = props.disabled;
  if (props.attrs) for (const [k, v] of Object.entries(props.attrs)) el.setAttribute(k, v);
  if (props.style) Object.assign(el.style, props.style);
  if (props.on) {
    for (const [type, fn] of Object.entries(props.on)) el.addEventListener(type, fn as EventListener);
  }
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    if (c instanceof Node) el.append(c);
    else if (typeof c === 'string' && c.includes(':')) el.append(...richNodes(c));
    else el.append(String(c));
  }
  return el;
}

export function clear(el: HTMLElement): void {
  while (el.firstChild) el.removeChild(el.firstChild);
}

/** A layer that blocks the game while visible. */
export function layer(className = ''): HTMLDivElement {
  const el = h('div', { class: `layer interactive ${className}`.trim(), hidden: true });
  return el;
}
